/**
 * Data Chart Creation Functions
 * Charts for facilities, service level, demand, capacity, etc.
 */

import Chart from 'chart.js/auto'

/**
 * Create facilities chart (bar chart by type)
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Object} card - Card object
 * @param {Object} chartData - Chart data with effectiveFacilities
 * @returns {Chart} Chart.js instance
 */
export function createFacilitiesChart(ctx, card, chartData) {
  const { effectiveFacilities } = chartData
  
  // Get facility counts by type
  const facilityCounts = {}
  effectiveFacilities.forEach(f => {
    const type = f.type || 'Other'
    facilityCounts[type] = (facilityCounts[type] || 0) + 1
  })
  
  const labels = Object.keys(facilityCounts)
  const data = Object.values(facilityCounts)
  
  return new Chart(ctx, {
    type: 'bar',
    data: {
      labels: labels,
      datasets: [{
        label: 'Number of Facilities',
        data: data,
        backgroundColor: '#4CAF50',
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: { beginAtZero: true, ticks: { stepSize: 1 } }
      },
      plugins: {
        title: {
          display: true,
          text: 'Facilities by Type'
        }
      }
    }
  })
}

/**
 * Create service level chart (doughnut)
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Object} card - Card object
 * @param {Object} chartData - Chart data with serviceLevelPct
 * @returns {Chart} Chart.js instance
 */
export function createServiceLevelChart(ctx, card, chartData) {
  const serviceLevel = chartData.serviceLevelPct || 0
  const shortfall = 100 - serviceLevel
  
  return new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: ['Met', 'Shortfall'],
      datasets: [{
        data: [serviceLevel, shortfall],
        backgroundColor: ['#8BC34A', '#FFEB3B'],
        borderWidth: 0
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '70%',
      plugins: {
        title: {
          display: true,
          text: 'Service Level Performance'
        },
        legend: {
          position: 'bottom'
        }
      }
    }
  })
}

/**
 * Create demand chart (bar with target line)
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Object} card - Card object
 * @param {Object} chartData - Chart data with demandFulfilledPct
 * @returns {Chart} Chart.js instance
 */
export function createCapacityChart(ctx, card, chartData) {
  const usedCapacity = chartData.utilizationPct || 0
  const spareCapacity = 100 - usedCapacity
  
  return new Chart(ctx, {
    type: 'bar',
    data: {
      labels: ['Capacity Distribution'],
      datasets: [
        {
          label: 'Used',
          data: [usedCapacity],
          backgroundColor: '#2196F3',
        },
        {
          label: 'Spare',
          data: [spareCapacity],
          backgroundColor: '#81C784',
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { stacked: true },
        y: { stacked: true, beginAtZero: true, max: 100 }
      },
      plugins: {
        title: {
          display: true,
          text: 'Capacity Usage'
        }
      }
    }
  })
}

/**
 * Create generic chart (fallback)
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {Object} card - Card object
 * @param {Object} chartData - Chart data
 * @returns {Chart} Chart.js instance
 */
export function createGenericChart(ctx, card, chartData) {
  return new Chart(ctx, {
    type: 'bar',
    data: {
      labels: ['Value'],
      datasets: [{
        label: card.label,
        data: [card.value],
        backgroundColor: card.color,
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: { beginAtZero: true }
      },
      plugins: {
        title: {
          display: true,
          text: card.label
        }
      }
    }
  })
}

















